import React, { useState, useEffect } from 'react';
import BookingsTab from '../components/admin/BookingsTab';
import RegistrationsTab from '../components/admin/RegistrationsTab';
import MarketingTab from '../components/admin/MarketingTab';
import HeritageTab from '../components/admin/HeritageTab';
import DraftsTab from '../components/admin/DraftsTab';
import CallbacksTab from '../components/admin/CallbacksTab';
import ReportsTab from '../components/admin/ReportsTab';
import StaffAppTab from '../components/admin/StaffAppTab';
import AnalyticsTab from '../components/admin/AnalyticsTab';
import { AlertCircle } from 'lucide-react';

const AdminBookingsPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [loginError, setLoginError] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [activeTab, setActiveTab] = useState('bookings');

  // Tab counts for badge display
  const [bookingsCount, setBookingsCount] = useState(0);
  const [registrationsCount, setRegistrationsCount] = useState(0);
  const [savedAdsCount, setSavedAdsCount] = useState(0);
  const [brochuresCount, setBrochuresCount] = useState(0);
  const [heritageCount, setHeritageCount] = useState(0);
  const [draftsCount, setDraftsCount] = useState(0);
  const [callbacksPendingCount, setCallbacksPendingCount] = useState(0);
  const [reportsCount, setReportsCount] = useState(0);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    document.title = 'Measured Livestock | Admin';
    const savedAuth = sessionStorage.getItem('adminAuthenticated');
    if (savedAuth === 'true') {
      setIsAuthenticated(true);
      fetchCounts();
    }
  }, []);

  const fetchCounts = async () => {
    // Fetch counts in parallel for tab badges
    const endpoints = [
      { url: '/api/admin/bookings', setter: (d) => setBookingsCount(d.length) },
      { url: '/api/admin/registrations', setter: (d) => setRegistrationsCount(d.length) },
      { url: '/api/admin/marketing/ads', setter: (d) => setSavedAdsCount(d.length) },
      { url: '/api/admin/marketing/brochures', setter: (d) => setBrochuresCount(d.length) },
      { url: '/api/admin/heritage/images', setter: (d) => setHeritageCount(d.length) },
      { url: '/api/admin/unsubmitted-quotes', setter: (d) => setDraftsCount(d.length) },
      { url: '/api/admin/callback-requests', setter: (d) => setCallbacksPendingCount(d.filter(r => r.status === 'pending').length) },
      { url: '/api/admin/reports/imports', setter: (d) => setReportsCount(d.length) },
    ];
    endpoints.forEach(async ({ url, setter }) => {
      try {
        const res = await fetch(`${BACKEND_URL}${url}`);
        if (res.ok) { const data = await res.json(); setter(data); }
      } catch (e) { /* silent */ }
    });
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoggingIn(true);
    setLoginError('');
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: passwordInput }),
      });
      if (!response.ok) throw new Error('Invalid password');
      const data = await response.json();
      setIsAuthenticated(true);
      sessionStorage.setItem('adminAuthenticated', 'true');
      sessionStorage.setItem('adminToken', data.token);
      fetchCounts();
    } catch (error) {
      setLoginError('Incorrect password. Please try again.');
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem('adminAuthenticated');
    sessionStorage.removeItem('adminToken');
    setPasswordInput('');
  };

  // Login screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#f4f4f0] to-[#e5ddd5] flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full">
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold text-[#1a4d2e] mb-2">Admin Access Required</h1>
            <p className="text-gray-600">Please enter the admin password to access the bookings dashboard.</p>
          </div>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">Password</label>
              <input
                type="password"
                id="password"
                value={passwordInput}
                onChange={(e) => setPasswordInput(e.target.value)}
                data-testid="admin-password-input"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1a4d2e] focus:border-transparent"
                placeholder="Enter admin password"
                required
                disabled={isLoggingIn}
              />
            </div>
            {loginError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-red-700 text-sm">{loginError}</p>
              </div>
            )}
            <button
              type="submit"
              disabled={isLoggingIn || !passwordInput.trim()}
              data-testid="admin-login-btn"
              className="w-full bg-[#1a4d2e] text-white py-3 rounded-lg hover:bg-[#2d6b47] transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoggingIn ? 'Authenticating...' : 'Access Dashboard'}
            </button>
          </form>
          <div className="mt-6 text-center">
            <p className="text-xs text-gray-500">This dashboard contains sensitive customer information and requires authentication.</p>
          </div>
        </div>
      </div>
    );
  }

  const tabs = [
    { key: 'bookings', label: 'Booking Requests', count: bookingsCount, style: 'default' },
    { key: 'registrations', label: 'Registrations', count: registrationsCount, style: 'default' },
    { key: 'marketing', label: 'Marketing', count: savedAdsCount + brochuresCount, style: 'default' },
    { key: 'heritage', label: 'Heritage Library', count: heritageCount, style: 'default', hideLabel: true },
    { key: 'drafts', label: 'Drafts', count: draftsCount, style: 'default' },
    { key: 'callbacks', label: 'Callbacks', count: callbacksPendingCount, style: 'orange' },
    { key: 'reports', label: 'Reports', count: reportsCount, style: 'green' },
    { key: 'analytics', label: 'Analytics', count: null, style: 'default' },
    { key: 'staffapp', label: 'Staff App', count: null, style: 'default' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#f4f4f0] to-[#e5ddd5]">
      <div className="main-dashboard">
        {/* Header */}
        <div className="bg-[#1a4d2e] text-white shadow-lg">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-xl sm:text-2xl md:text-3xl font-bold">Admin Bookings Dashboard</h1>
                <p className="mt-1 sm:mt-2 text-green-100 text-sm sm:text-base">View and manage all bookings</p>
              </div>
              <button
                onClick={handleLogout}
                data-testid="admin-logout-btn"
                className="bg-red-600 hover:bg-red-700 text-white px-3 sm:px-4 py-2 rounded-lg transition-colors font-semibold text-sm sm:text-base"
              >
                Logout
              </button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-4 sm:mt-6">
          <div className="bg-white rounded-lg shadow-md p-2 flex gap-1 sm:gap-2 overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                data-testid={`tab-${tab.key}`}
                className={`px-3 sm:px-6 py-2 sm:py-3 rounded-lg font-semibold transition-colors whitespace-nowrap text-xs sm:text-sm md:text-base ${
                  activeTab === tab.key
                    ? tab.style === 'orange'
                      ? 'bg-[#ff6b00] text-white'
                      : tab.style === 'green'
                      ? 'bg-[#1a4d2e] text-white'
                      : 'bg-[#1a4d2e] text-white'
                    : tab.style === 'orange'
                    ? 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                    : tab.style === 'green'
                    ? 'bg-green-100 text-green-700 hover:bg-green-200'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {tab.hideLabel ? (
                  <>
                    <span className="hidden sm:inline">{tab.label} </span>({tab.count})
                  </>
                ) : tab.count !== null ? (
                  `${tab.label} (${tab.count})`
                ) : (
                  tab.label
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'bookings' && <BookingsTab BACKEND_URL={BACKEND_URL} />}
        {activeTab === 'registrations' && <RegistrationsTab BACKEND_URL={BACKEND_URL} />}
        {activeTab === 'marketing' && <MarketingTab BACKEND_URL={BACKEND_URL} />}
        {activeTab === 'heritage' && <HeritageTab BACKEND_URL={BACKEND_URL} />}
        {activeTab === 'drafts' && <DraftsTab BACKEND_URL={BACKEND_URL} />}
        {activeTab === 'callbacks' && <CallbacksTab BACKEND_URL={BACKEND_URL} />}
        {activeTab === 'reports' && <ReportsTab BACKEND_URL={BACKEND_URL} />}
        {activeTab === 'analytics' && <AnalyticsTab BACKEND_URL={BACKEND_URL} />}
        {activeTab === 'staffapp' && <StaffAppTab BACKEND_URL={BACKEND_URL} />}
      </div>
    </div>
  );
};

export default AdminBookingsPage;
